/**
 * Procedural, tileable noise fields for the sky.
 *
 * Everything here runs once at boot on the CPU and is deterministic (integer
 * hashing only — no Math.random). Zero external assets: the cloud "photograph"
 * the sky shader samples is built here, texel by texel.
 */
import * as THREE from 'three';

/* ------------------------------------------------------------------ hashing */
function hash2i(x, y, s) {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Seamless value-noise fbm over an N×N torus, returned in [0,1]. */
export function tileFbm(N, baseCells, seed, oct = 5, gain = 0.5, warp = 0) {
  const out = new Float32Array(N * N);
  let amp = 1, norm = 0;
  for (let o = 0; o < oct; o++) { norm += amp; amp *= gain; }
  amp = 1;
  let cells = baseCells;
  for (let o = 0; o < oct; o++) {
    const sc = cells / N, sd = seed + o * 7919;
    for (let j = 0; j < N; j++) {
      const fy = j * sc, jy = Math.floor(fy), ty = fy - jy;
      const sy = ty * ty * (3 - 2 * ty);
      const j0 = ((jy % cells) + cells) % cells, j1 = (j0 + 1) % cells;
      const row = j * N;
      for (let i = 0; i < N; i++) {
        const fx = i * sc, ix = Math.floor(fx), tx = fx - ix;
        const sx = tx * tx * (3 - 2 * tx);
        const i0 = ((ix % cells) + cells) % cells, i1 = (i0 + 1) % cells;
        const a = hash2i(i0, j0, sd), b = hash2i(i1, j0, sd);
        const c = hash2i(i0, j1, sd), d = hash2i(i1, j1, sd);
        const ab = a + (b - a) * sx, cd = c + (d - c) * sx;
        out[row + i] += amp * (ab + (cd - ab) * sy);
      }
    }
    amp *= gain; cells *= 2;
  }
  for (let k = 0; k < out.length; k++) out[k] /= norm;
  return out;
}

/** Bilinear fetch from a wrapped N×N float field. */
function sampleField(f, N, u, v) {
  let x = u * N, y = v * N;
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const x0 = ((xi % N) + N) % N, x1 = (x0 + 1) % N;
  const y0 = ((yi % N) + N) % N, y1 = (y0 + 1) % N;
  const a = f[y0 * N + x0], b = f[y0 * N + x1], c = f[y1 * N + x0], d = f[y1 * N + x1];
  const ab = a + (b - a) * fx, cd = c + (d - c) * fx;
  return ab + (cd - ab) * fy;
}

/**
 * The cloud sheet. One tileable RGBA texture the sky shader reads:
 *
 *   R  cumulus body      — domain-warped fbm, billowy, the main silhouette
 *   G  erosion detail    — high frequency, nibbles the edges so they are not
 *                          smooth blobs
 *   B  cirrus            — stretched, wispy, for the high layer
 *   A  soft body         — a blurred copy of R used as a cheap optical-depth
 *                          estimate when lighting the cloud towards the sun
 */
export function buildCloudTexture(N = 512, seed = 1337) {
  const base = tileFbm(N, 4, seed, 6, 0.52);
  const warpX = tileFbm(N, 3, seed + 101, 4, 0.5);
  const warpY = tileFbm(N, 3, seed + 202, 4, 0.5);
  const billow = tileFbm(N, 8, seed + 303, 5, 0.55);
  const detail = tileFbm(N, 24, seed + 404, 4, 0.5);
  const cir = tileFbm(N, 2, seed + 505, 5, 0.58);
  const cirDetail = tileFbm(N, 10, seed + 606, 4, 0.5);

  const body = new Float32Array(N * N);
  const data = new Uint8Array(N * N * 4);

  for (let j = 0; j < N; j++) {
    const v = j / N;
    for (let i = 0; i < N; i++) {
      const u = i / N;
      const k = j * N + i;
      // domain warp: pushes the fbm around so the shapes stop looking like fbm
      const wx = (warpX[k] - 0.5) * 0.22;
      const wy = (warpY[k] - 0.5) * 0.22;
      const b0 = sampleField(base, N, u + wx, v + wy);
      const b1 = sampleField(billow, N, u + wx * 1.7, v + wy * 1.7);
      // billow ridges give cauliflower tops instead of soft gaussian mush
      let d = b0 * 0.68 + (1.0 - Math.abs(b1 * 2 - 1)) * 0.32;
      // vertical squash so the sheet has structure rather than uniform speckle
      d = d * (0.86 + 0.28 * sampleField(base, N, u * 0.25, v * 0.25));
      body[k] = d;
    }
  }

  // Value-noise fbm piles up around its mean: raw, `body` only ever spans
  // roughly 0.3-0.7, so any coverage threshold in the sky shader either
  // catches everything or nothing and the dome renders as a bare gradient.
  // Renormalise to the full range and put a mild S on it so the sheet has
  // actual cloud silhouettes with clear air between them.
  let lo = Infinity, hi = -Infinity;
  for (let k = 0; k < body.length; k++) {
    const b = body[k];
    if (b < lo) lo = b;
    if (b > hi) hi = b;
  }
  const span = 1 / Math.max(1e-4, hi - lo);
  for (let k = 0; k < body.length; k++) {
    const t = (body[k] - lo) * span;
    body[k] = t * t * (3 - 2 * t);
  }

  // cheap separable blur for the soft/optical-depth channel
  const soft = new Float32Array(N * N);
  const tmp = new Float32Array(N * N);
  const R = 6;
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      let s = 0;
      for (let t = -R; t <= R; t++) s += body[j * N + (((i + t) % N) + N) % N];
      tmp[j * N + i] = s / (R * 2 + 1);
    }
  }
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      let s = 0;
      for (let t = -R; t <= R; t++) s += tmp[((((j + t) % N) + N) % N) * N + i];
      soft[j * N + i] = s / (R * 2 + 1);
    }
  }

  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const k = j * N + i, o = k * 4;
      // cirrus: heavily stretched in u so it streaks along the wind
      const cu = (i / N) * 0.35, cv = (j / N) * 1.9;
      let c = sampleField(cir, N, cu, cv) * 0.7 + sampleField(cirDetail, N, cu * 2.2, cv * 2.2) * 0.3;
      c = Math.max(0, Math.min(1, (c - 0.40) * 3.2));
      data[o] = Math.max(0, Math.min(255, body[k] * 255)) | 0;
      data[o + 1] = Math.max(0, Math.min(255, detail[k] * 255)) | 0;
      data[o + 2] = Math.max(0, Math.min(255, c * 255)) | 0;
      data[o + 3] = Math.max(0, Math.min(255, soft[k] * 255)) | 0;
    }
  }

  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** 4-channel blue-ish noise tile used to break up SSAO/grain patterns. */
export function buildBlueNoise(N = 64, seed = 7717) {
  const data = new Uint8Array(N * N * 4);
  // void-and-cluster is overkill here; a hashed white noise decorrelated per
  // channel is enough once the AO kernel rotates by it.
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const o = (j * N + i) * 4;
      data[o] = (hash2i(i, j, seed) * 255) | 0;
      data[o + 1] = (hash2i(i, j, seed + 811) * 255) | 0;
      data[o + 2] = (hash2i(i, j, seed + 1621) * 255) | 0;
      data[o + 3] = (hash2i(i, j, seed + 2437) * 255) | 0;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}
